import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaTimes, FaSave, FaSyncAlt, FaSearch } from 'react-icons/fa';
import { ui } from '../utils/styles';
import { MUNICIPIOS_BRASIL } from '../utils/cidadesBrasil';
import { ESTADOS_BRASILEIROS } from '../utils/ufsBrasil';

const DevedorModal = ({ show, onClose, devedor, onSave, mode, clientesContratantes }) => {

    // --- Estados Locais do Formulário ---
    const [nome, setNome] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [status, setStatus] = useState('Ativo');
    const [tipoPessoa, setTipoPessoa] = useState('PESSOA_FISICA');
    const [endereco, setEndereco] = useState('');
    const [bairro, setBairro] = useState('');
    const [numero, setNumero] = useState('');
    const [cep, setCep] = useState('');
    const [uf, setUf] = useState('');
    const [idUf, setIdUf] = useState(0);
    const [ibge, setIbge] = useState('');
    const [cidade, setCidade] = useState('');
    const [idCidade, setIdCidade] = useState(0);
    const [email, setEmail] = useState('');
    const [foneContato, setFoneContato] = useState('');

    const [loading, setLoading] = useState(false);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    // --- Efeitos de Inicialização e Reset ---
    useEffect(() => {
        if (show) {
            if (devedor && (mode === 'edit' || mode === 'view')) {
                setNome(devedor.nome || devedor.razao || '');
                setCpfCnpj(devedor.cpf || devedor.cnpj || '');
                setStatus(devedor.status || 'Ativo');
                setTipoPessoa(devedor.tipo_pessoa || 'PESSOA_FISICA');
                setEndereco(devedor.endereco || '');
                setBairro(devedor.bairro || '');
                setNumero(devedor.numero || '');
                setCep(devedor.cep || '');
                setEmail(devedor.email1 || '');
                setFoneContato(devedor.foneContato || '');
                setUf(devedor.uf || '');
                setCidade(devedor.municipio || '');
                setIdCidade(devedor.id_cidade || 0);
                setIbge(devedor.ibge || '');
                setIdUf(devedor.id_uf || 0);
            } else {
                setNome('');
                setCpfCnpj('');
                setStatus('Ativo');
                setTipoPessoa('PESSOA_FISICA');
                setEndereco('');
                setBairro('');
                setNumero('');
                setCep('');
                setEmail('');
                setFoneContato('');
                setUf('');
                setCidade('');
                setIdCidade(0);
                setIdUf(0);
                setIbge('');
            }
        }
    }, [show, devedor, mode]);

    const formatCpfCnpj = (value) => {
        if (!value) return '';
        const digits = value.replace(/\D/g, '');
        if (digits.length <= 11) {
            return digits
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            return digits
                .replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/\.(\d{3})(\d)/, '.$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2');
        }
    };

    const handleUfCidade = (u, c) => {
        try {
            const estado = ESTADOS_BRASILEIROS.find(
                (e) => e.sigla.toUpperCase() === u.toUpperCase()
            );
            if (!estado) return null;

            const cid = MUNICIPIOS_BRASIL.find(
                (m) => m.nome.toUpperCase() === c.toUpperCase() && m.codigo_uf === estado.id
            );
            if (!cid) return null;

            setIdUf(estado.id);
            setIdCidade(cid.id);
            setIbge(cid.codigo_ibge);
        } catch (error) {
            console.error("Erro ao processar localidade:", error);
        }
    };

    const handleSearchReceita = async () => {
        const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');
        if (!cleanCpfCnpj || (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14)) {
            toast.error('Informe um CPF ou CNPJ válido.');
            return;
        }

        setLoading(true);
        const toastId = toast.loading('Consultando...');

        try {
            const { data } = await axios.get(`${URL_BACKEND}/consultaCnpj/${cleanCpfCnpj}`, { headers: authHeader });
            if (data) {
                setCpfCnpj(data.cnpj || data.cpf || cleanCpfCnpj);
                setNome(data.razao_social || data.nome || nome);
                setTipoPessoa(data.cnpj ? 'PESSOA_JURIDICA' : 'PESSOA_FISICA');
                setEndereco(data.logradouro || '');
                setBairro(data.bairro || '');
                setCep(data.cep || '');
                setUf(data.uf || '');
                setCidade(data.municipio || '');
                await handleUfCidade(data.uf, data.municipio);
                toast.success('Dados preenchidos!', { id: toastId });
            }
        } catch (error) {
            toast.error('Erro ao buscar dados.', { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // Removido clienteContratanteId da validação
        if (!nome || !cpfCnpj || !email || !foneContato) {
            toast.error('Preencha os campos obrigatórios (*).');
            return;
        }

        const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');
        const payload = {
            tipo_pessoa: cleanCpfCnpj.length <= 11 ? 'PESSOA_FISICA' : 'PESSOA_JURIDICA',
            cleanCpfCnpj,
            nome,
            razao: nome,
            status,
            endereco,
            bairro,
            numero,
            cep,
            id_cidade: Number(idCidade),
            id_uf: Number(idUf),
            uf,
            ibge: ibge,
            email1: email,
            foneContato,
            municipio: cidade,
            cnpj: cpfCnpj
        };

        setLoading(true);
        const toastId = toast.loading('Salvando...');

        try {
            if (mode === 'create') {
                await axios.post(`${URL_BACKEND}/devedor`, payload, { headers: authHeader });
                toast.success('Devedor cadastrado!', { id: toastId });
            } else if (mode === 'edit') {
                await axios.put(`${URL_BACKEND}/devedor/${devedor.id}`, payload, { headers: authHeader });
                toast.success('Devedor atualizado!', { id: toastId });
            }
            onSave();
            onClose();
        } catch (error) {
            toast.error('Erro ao salvar.', { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;
    const isViewMode = mode === 'view';

    return (
        <div style={ui.modalOverlay}>
            <div style={ui.modalContent}>
                <div style={ui.modalHeader}>
                    <h2 style={ui.modalTitle}>
                        {mode === 'create' ? 'Novo Devedor' : mode === 'edit' ? `Editar Devedor #${devedor?.id}` : `Visualizar Devedor #${devedor?.id}`}
                    </h2>
                    <button onClick={onClose} style={ui.modalCloseButton} disabled={loading}><FaTimes /></button>
                </div>

                <form onSubmit={handleSave} style={ui.modalBody}>
                    <div style={ui.formGrid}>

                        {/* 1. CPF/CNPJ */}
                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>CPF/CNPJ *</label>
                            <div style={{ display: 'flex', gap: 5 }}>
                                <input
                                    style={ui.input}
                                    value={formatCpfCnpj(cpfCnpj)}
                                    onChange={e => setCpfCnpj(e.target.value)}
                                    disabled={isViewMode || loading}
                                    maxLength={18}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={handleSearchReceita}
                                    style={{ ...ui.buttonPrimary, background: '#a855f7', padding: '0 15px' }}
                                    disabled={isViewMode || loading}
                                >
                                    {loading ? <FaSyncAlt className="fa-spin" /> : <FaSearch />}
                                </button>
                            </div>
                        </div>

                        {/* 2. Status */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>Status</label>
                            <select style={ui.select} value={status} onChange={e => setStatus(e.target.value)} disabled={isViewMode || loading}>
                                <option value="Ativo">Ativo</option>
                                <option value="Inativo">Inativo</option>
                            </select>
                        </div>

                        {/* 3. Nome/Razão Social */}
                        <div style={{ ...ui.formGroup, gridColumn: 'span 3' }}>
                            <label style={ui.label}>Nome/Razão Social *</label>
                            <input style={ui.input} value={nome} onChange={e => setNome(e.target.value)} disabled={isViewMode || loading} required />
                        </div>

                        {/* 4. Email */}
                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>Email *</label>
                            <input type="email" style={ui.input} value={email} onChange={e => setEmail(e.target.value)} disabled={isViewMode || loading} required />
                        </div>

                        {/* 5. Telefone */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>Fone Contato *</label>
                            <input type="tel" style={ui.input} value={foneContato} onChange={e => setFoneContato(e.target.value)} disabled={isViewMode || loading} required />
                        </div>

                        {/* ENDEREÇO */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>CEP</label>
                            <input style={ui.input} value={cep} onChange={e => setCep(e.target.value)} disabled={isViewMode || loading} maxLength={9} />
                        </div>

                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>Endereço</label>
                            <input style={ui.input} value={endereco} onChange={e => setEndereco(e.target.value)} disabled={isViewMode || loading} />
                        </div>

                        <div style={ui.formGroup}>
                            <label style={ui.label}>Nº</label>
                            <input style={ui.input} value={numero} onChange={e => setNumero(e.target.value)} disabled={isViewMode || loading} required />
                        </div>

                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>Bairro</label>
                            <input style={ui.input} value={bairro} onChange={e => setBairro(e.target.value)} disabled={isViewMode || loading} required />
                        </div>

                        <div style={ui.formGroup}>
                            <label style={ui.label}>UF</label>
                            <input style={ui.input} value={uf} disabled />
                        </div>

                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>Cidade</label>
                            <input style={ui.input} value={cidade} disabled />
                        </div>
                    </div>

                    <div style={ui.modalFooter}>
                        <button type="button" onClick={onClose} style={ui.buttonLight} disabled={loading}>
                            {isViewMode ? 'Fechar' : 'Cancelar'}
                        </button>
                        {(mode === 'create' || mode === 'edit') && (
                            <button type="submit" style={{ ...ui.buttonPrimary, background: '#047857' }} disabled={loading}>
                                {loading ? <FaSyncAlt className="fa-spin" /> : <FaSave />}
                                {mode === 'create' ? ' Cadastrar' : ' Salvar'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DevedorModal;